import type { FC } from "react";

import { CmsError, NoContent, PageLayout } from "src/components";
import { useProjectsInfoQuery } from "src/serverApi";
import { useTranslation } from "src/translations";
import type { ProjectInfo } from "src/types";
import { BannerPageEnum, ProjectInfoStateEnum } from "src/types";
import { ProjectCard } from "./components";

export const ProjectsPage: FC = () => {
    const { t } = useTranslation();
    const { data: projectsInfo, isLoading, isFetching, isError, error } = useProjectsInfoQuery(undefined);

    const loading = isLoading || isFetching;

    return (
        <PageLayout title={t("projects.list.title")} loading={loading} banner={BannerPageEnum.Projects}>
            {isError ? <CmsError error={error} fallbackMessage={t("common.error")} /> : null}
            {!isError && !loading && !projectsInfo?.data?.length && <NoContent transKey="projects.list.noProjects" />}
            <div className="mx-auto w-full max-w-[1250px] space-y-8">
                {projectsInfo?.data
                    ?.toSorted((a, b) => {
                        // sort by project's state…
                        const firstStateScore = stateScore(a.project?.state);
                        const secondStateScore = stateScore(b.project?.state);
                        if (firstStateScore !== secondStateScore) {
                            return firstStateScore - secondStateScore;
                        }
                        // …and by creation date from the newest to the oldest if both projects have the same state
                        return createdAt(b).getTime() - createdAt(a).getTime();
                    })
                    ?.map((projectInfo) => <ProjectCard key={projectInfo.id} projectInfo={projectInfo} />)}
            </div>
        </PageLayout>
    );
};

const stateScore = (state: ProjectInfoStateEnum | undefined): number => {
    switch (state || ProjectInfoStateEnum.Unknown) {
        case ProjectInfoStateEnum.Open:
            return 0;
        case ProjectInfoStateEnum.Prepared:
            return 1;
        case ProjectInfoStateEnum.Completed:
            return 2;
        case ProjectInfoStateEnum.Closed:
            return 3;
        case ProjectInfoStateEnum.Unknown:
        default:
            return 10;
    }
};

const createdAt = (projectInfo: ProjectInfo): Date => {
    const projectCreatedAt = projectInfo.project?.createdAt;
    if (projectCreatedAt) {
        return new Date(projectCreatedAt);
    }
    return new Date(0);
};
